import { useMemo, useState } from 'react';
import { SearchCodeModalForm } from 'src/components/common/form/search/SearchCodeModalForm';
import { SearchDropDownForm } from 'src/components/common/form/search/SearchDropDownForm';
import { SearchSectionForm } from 'src/components/common/form/search/SearchSectionForm';
import { SearchYearSingleForm } from 'src/components/common/form/search/SearchYearSingleForm';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore from 'src/store/useUiuxStore';
import { useHRM1030_R01Store } from './Store_HRM1030_R01';

/**
 * 교육년도·교육구분·사업장·부서·재직상태·사원 검색 조건을 표시한다.
 * @returns 검색 조건 입력 영역 JSX
 */
export const HRM1030_R01SearchSection = () => {
    // 화면 Store에서 현재 검색 조건과 변경 함수를 가져온다.
    const { HRM1030_R01Filter, setHRM1030_R01Filter } = useHRM1030_R01Store();
    // 앱 초기화 때 읽어 둔 교육구분(TRAIN_TYPE)·사업장·부서·재직상태 공통코드 목록을 가져온다.
    const { trainTypeList, compCdList, orgCdList, empstsCdList } = useCommonCodeStore();
    // 공통 코드 검색 팝업을 여는 함수를 가져온다.
    const { setCodeSearchModalProps } = useUiuxStore();
    // 연도 선택 달력의 표시 여부와 위치 (EQM3020 SearchSection과 같은 방식)
    const [isYearPickerVisible, setIsYearPickerVisible] = useState(false);
    const [yearPickerPosition, setYearPickerPosition] = useState({ top: 0, left: 0 });
    // 연도 선택기에 넘길 Date 값. 교육년도 문자열(YYYY)이 바뀔 때만 다시 만든다.
    const trainYearValue = useMemo(() => {
        const year = Number(HRM1030_R01Filter.trainYear);
        // 삼항 연산자: 4자리 연도면 그해 1월 1일, 아니면 값 없음
        return year >= 1000 && year <= 9999 ? new Date(year, 0, 1) : undefined;
    }, [HRM1030_R01Filter.trainYear]);

    /** 공통 사원 검색 팝업을 열고 선택한 사번·이름을 검색 조건에 기록한다(HRM1040과 같음). */
    const openEmployeeSearch = (search: string) => {
        setCodeSearchModalProps({
            category: '사원',
            search,
            formName: 'HRM1030_R01',
            isInfo: false,
            handleFoucsId: 'HRM1030_R01-employee-search',
            // ?.·||: 선택한 사원이 없으면 빈 값으로 둔다.
            onConfirm: employee => setHRM1030_R01Filter({
                keyword: employee?.empNo || '',
                empNm: employee?.empNm || '',
            }),
            processType_focus: 'normal',
        });
    };
    return (
        <SearchSectionForm>
            {/* 교육년도: 시작일 기준으로 사원 집계와 교육 이력을 거른다. */}
            <SearchYearSingleForm
                label="교육년도"
                visible={isYearPickerVisible}
                setVisible={setIsYearPickerVisible}
                value={trainYearValue}
                onChange={date => {
                    if (date) setHRM1030_R01Filter({ trainYear: String(date.getFullYear()) });
                }}
                pickerPosition={yearPickerPosition}
                setPickerPosition={setYearPickerPosition}
            />
            {/* 교육구분: TRAIN_TYPE 공통코드. "전체"이면 모든 구분 */}
            <SearchDropDownForm
                label="교육구분"
                value={HRM1030_R01Filter.trainType}
                optionList={trainTypeList}
                onChangeItem={item => setHRM1030_R01Filter({ trainType: item?.minorCd || '' })}
            />
            <SearchDropDownForm
                label="사업장"
                value={HRM1030_R01Filter.compCd}
                optionList={compCdList}
                onChangeItem={item => setHRM1030_R01Filter({ compCd: item?.compCd || '' })}
            />
            <SearchDropDownForm
                label="부서"
                value={HRM1030_R01Filter.orgCd}
                optionList={orgCdList}
                onChangeItem={item => setHRM1030_R01Filter({ orgCd: item?.orgCd || '' })}
            />
            <SearchDropDownForm
                label="재직상태"
                value={HRM1030_R01Filter.empSts}
                optionList={empstsCdList}
                onChangeItem={item => setHRM1030_R01Filter({ empSts: item?.minorCd || '' })}
            />
            {/* 사원: 직접 입력하거나 더블클릭·Enter로 공통 사원 검색 팝업을 연다. */}
            <SearchCodeModalForm
                label="사원"
                value={HRM1030_R01Filter.keyword}
                valueLabel={HRM1030_R01Filter.empNm}
                onChange={event => setHRM1030_R01Filter({ keyword: event.target.value, empNm: '' })}
                onDoubleClick={event => {
                    event.currentTarget.blur();
                    openEmployeeSearch(HRM1030_R01Filter.keyword);
                }}
                onKeyDown={event => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    openEmployeeSearch(HRM1030_R01Filter.keyword);
                }}
            />
        </SearchSectionForm>
    );
};
